import * as React from 'react';
import {
    Button,
    Form,
    Select,
    Upload,
    message,
    Col,
    Row,
} from 'antd';
import {
    UploadOutlined,
    PlusOutlined,
    DeleteOutlined,
    LinkOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FormField } from '../../create-consignment.types';
import Typography from 'antd/lib/typography';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { ReduxStore } from 'reducers/redux.types';
import { StylesProps } from '../../../../theme/jss-types';
import { FormInstance } from 'antd/lib/form';
import { documentUploadFieldStyles } from './document-upload-field.styles';

const { Text } = Typography;

interface DocumentDetails {
    document_type: string;
    new_files_uploaded: any[];
}

interface Props extends StylesProps<typeof documentUploadFieldStyles> {
    form: FormInstance;
    field: FormField;
    classes: any;
    master: any;
    config: any;
    params?: any;
}

const DocumentUploadField = (props: Props) => {
    const { t } = useTranslation();
    const {
        form,
        field,
        classes,
        master,
        config,
        params,
    } = props;
    const { disabledFields = {} } = params || {};
    const isFieldDisabled = !!disabledFields[field.key];
    const [documentDetails, setDocumentDetails] = React.useState<DocumentDetails[]>([]);

    const documentConfig = master?.config?.customer_portal_ops_config?.document_config?.config_value;
    const validDocumentTypes = documentConfig?.allowed_document_types_customer_portal || [];
    const documentIdToNameMapping = master?.config?.available_document_types_id_to_name_mapping || {};
    const maxFiles = config?.allow_multiple_file_upload_in_customer_portal?.number_of_documents_allowed_in_consignment;

    const availableDocumentTypesList = validDocumentTypes?.map(
        (documentType: any) => {
            return {
                label: documentIdToNameMapping[documentType],
                value: documentType,
            };
        },
    );

    const getTotalFilesCount = () => {
        let currentTotalFiles = 0;
        documentDetails?.forEach((data: any) => {
            currentTotalFiles += data?.new_files_uploaded?.length || 0;
        });
        return currentTotalFiles;
    };

    const isUploadDisabled = () => {
        return isFieldDisabled || getTotalFilesCount() >= maxFiles;
    };

    const handleDocumentTypeChange = (value: string, index: number) => {
        const updatedDocumentDetails = [...(documentDetails || [])];
        updatedDocumentDetails[index] = {
            ...(updatedDocumentDetails[index] || {}),
            document_type: value,
        };
        setDocumentDetails(updatedDocumentDetails);
        form.setFieldsValue({
            [field.key]: updatedDocumentDetails,
        });
    };

    const handleChange = (info: any, index: number) => {
        const file = info?.file;
        if (file && file?.size / 1024 / 1024 < 2) {
            // Calculate current total files excluding the current document type
            let currentTotalFiles = 0;
            documentDetails?.forEach((data: any, idx: number) => {
                if (idx !== index) {
                    currentTotalFiles += data?.new_files_uploaded?.length || 0;
                }
            });

            // Add the new files being uploaded
            const newFilesCount = info?.fileList?.length || 0;
            const totalFilesAfterUpload = currentTotalFiles + newFilesCount;

            if (totalFilesAfterUpload > maxFiles) {
                message.error(`Maximum ${maxFiles} files allowed in total`);
                return;
            }

            const updatedDocumentDetails = [...(documentDetails || [])];
            updatedDocumentDetails[index] = {
                ...(updatedDocumentDetails[index] || {}),
                new_files_uploaded: info?.fileList,
            };
            setDocumentDetails(updatedDocumentDetails);
            form.setFieldsValue({
                [field.key]: updatedDocumentDetails,
            });
        }
    };

    const handleRemove = (file: any, docIndex: number) => {
        const updatedDocumentDetails = [...(documentDetails || [])];
        updatedDocumentDetails[docIndex] = {
            ...(updatedDocumentDetails[docIndex] || {}),
            new_files_uploaded: updatedDocumentDetails[docIndex]?.new_files_uploaded?.filter(
                (f: any) => f?.uid !== file?.uid,
            ),
        };
        setDocumentDetails(updatedDocumentDetails);
        form.setFieldsValue({
            [field.key]: updatedDocumentDetails,
        });
    };

    const addNewDocument = () => {
        if (isUploadDisabled()) {
            message.error(`Maximum ${maxFiles} files allowed in total`);
            return;
        }
        setDocumentDetails([
            ...(documentDetails || []),
            { document_type: '', new_files_uploaded: [] },
        ]);
    };

    const removeDocument = (index: number) => {
        const newDocumentDetails = [...(documentDetails || [])];
        newDocumentDetails?.splice(index, 1);
        setDocumentDetails(newDocumentDetails);
    };


    const renderFiles = (doc: DocumentDetails, docIndex: number) => {
        const rows = [];
        for (let i = 0; i < doc?.new_files_uploaded?.length; i += 3) {
            rows?.push(doc?.new_files_uploaded?.slice(i, i + 3));
        }

        return rows?.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
                {rows?.map((row, rowIndex) => {
                    const rowKey = `${doc.document_type}-${docIndex}-${rowIndex}`;
                    return (
                        <Row
                            key={rowKey}
                            gutter={[8, 8]}
                        >
                            {row?.map((file, fileIndex) => {
                                const fileKey = `${doc?.document_type}-${docIndex}-${file?.name}-${fileIndex}`;
                                return (
                                    <Col
                                        key={fileKey}
                                        span={8}
                                        style={{ display: 'contents' }}
                                    >
                                        <div className={classes.customFlex}>
                                            <div className={classes.customFlex}>
                                                <LinkOutlined
                                                    style={{
                                                        color: '#666666',
                                                        marginLeft: fileIndex === 0 ? '19px' : '41px',
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        marginLeft: '8px',
                                                        marginRight: '8px',
                                                        maxWidth: '135px',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                        color: '#666666',
                                                    }}
                                                >
                                                    {file?.name}
                                                </Text>
                                            </div>
                                            <DeleteOutlined
                                                style={{ color: '#666666', cursor: 'pointer' }}
                                                onClick={() => handleRemove(file, docIndex)}
                                            />
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    );
                })}
            </div>
        );
    };

    const renderDocumentRow = (doc: DocumentDetails, index: number) => {
        const docKey = `${doc?.document_type}-${index}`;

        return (
            <div key={docKey}>
                <div className={classes.documentRow}>
                    <div className={classes.grayCircle}>
                        {index + 1}
                    </div>
                    <div className={classes.selectContainer}>
                        <Select
                            placeholder={t('Choose Document Type')}
                            value={doc?.document_type}
                            onChange={(value) => handleDocumentTypeChange(value, index)}
                            style={{ width: '100%' }}
                            options={availableDocumentTypesList}
                            disabled={isUploadDisabled()}
                        />
                    </div>
                    <div className={classes.uploadContainer}>
                        <Upload
                            name="file"
                            disabled={isUploadDisabled()}
                            fileList={doc?.new_files_uploaded}
                            onChange={(info) => handleChange(info, index)}
                            onRemove={(file) => handleRemove(file, index)}
                            multiple
                            beforeUpload={(file) => {
                                const isLt2M = file?.size / 1024 / 1024 < 2;
                                if (!isLt2M) {
                                    message.error('File must be smaller than 2MB!');
                                }
                                return isLt2M;
                            }}
                            showUploadList={false}
                        >
                            <Button
                                disabled={isUploadDisabled()}
                                icon={<UploadOutlined />}
                            >
                                Upload Files
                            </Button>
                        </Upload>
                    </div>
                    <DeleteOutlined
                        style={{
                            color: isFieldDisabled ? '#cccccc' : '#666666',
                            fontSize: '20px',
                            cursor: isFieldDisabled ? 'not-allowed' : 'pointer',
                        }}
                        onClick={() => {
                            if (isFieldDisabled) return;
                            removeDocument(index);
                        }}
                    />
                </div>
                {renderFiles(doc, index)}
            </div>
        );
    };

    const renderAddDocumentTypeButton = () => {
        return (
            <Button
                type="primary"
                className={classes.addDocumentButton}
                icon={<PlusOutlined />}
                onClick={addNewDocument}
                disabled={isFieldDisabled}
            >
                {t('Add Document Type')}
            </Button>
        );
    };

    return (
        <Col span={24} className={classes.container}>
            <div className={classes.sectionTitle}>
                <FileTextOutlined style={{ fontSize: '24px' }} />
                <span className={classes.uploadText}>{t('Upload Documents')}</span>
            </div>
            <Form.Item name={field?.key}>
                <div>
                    {documentDetails?.map((doc, index) => renderDocumentRow(doc, index))}
                    {documentDetails?.length < 5 && renderAddDocumentTypeButton()}
                    <div className={classes.helpText}>
                        {`Maximum ${maxFiles} files can be uploaded. Formats supported are pdf, jpg, jpeg, excel.`}
                    </div>
                </div>
            </Form.Item>
        </Col>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { config } = master;

    return {
        config,
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: documentUploadFieldStyles,
    },
};

export default GenericHoc(hocConfig)(DocumentUploadField);

